import { Link } from "react-router-dom";
import { WEB_APP_URL } from "./config";

/**
 * Shared main content for training provider onboarding pages (after the hero).
 */
export default function TrainingProviderOnboardingBody({
  shareUrl,
  partnershipEmail,
}) {
  const mailto = partnershipEmail;

  return (
    <>
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-3xl font-bold mb-6">What is Fit-Match?</h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            Fit-Match is a UK fitness platform that connects{" "}
            <strong className="text-white">clients</strong>,{" "}
            <strong className="text-white">qualified professionals</strong>,{" "}
            <strong className="text-white">communities</strong>, and{" "}
            <strong className="text-white">facilities</strong> in one place —
            with map-based discovery, profiles, messaging, and (for full
            members) bookings and payments through Stripe.
          </p>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            There are <strong className="text-white">no booking fees</strong>{" "}
            charged to clients for connecting through the platform; professionals
            choose membership plans that match how they want to work.
          </p>
          <p className="text-gray-400 text-base leading-relaxed border-l-4 border-blue-fit-match pl-4">
            Fit-Match is in its{" "}
            <strong className="text-gray-300">early stages</strong>
            — we are building openly with partners and users. The community is{" "}
            <strong className="text-gray-300">growing steadily</strong>: more
            professionals, clients, and organisations joining the map every
            month. Early partners help shape the product while giving their
            graduates a head start on a platform designed for the long term.
          </p>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl font-bold mb-4 text-center">
            How it works for clients
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12">
            People looking for trainers, coaches, physios, or classes use
            Fit-Match to discover and contact providers near them.
          </p>
          <ul className="space-y-4 text-gray-300 max-w-2xl mx-auto">
            <li className="flex gap-3">
              <span className="text-blue-fit-match font-bold shrink-0">1.</span>
              <span>
                <strong className="text-white">Explore the map</strong> to see
                professionals, facilities, communities, and training providers by
                location.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-blue-fit-match font-bold shrink-0">2.</span>
              <span>
                <strong className="text-white">Open profiles</strong> — photos,
                services, reviews, and credentials — to compare options.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-blue-fit-match font-bold shrink-0">3.</span>
              <span>
                <strong className="text-white">Message or book</strong> —
                direct messaging is built in; professionals on{" "}
                <strong className="text-white">Full Membership</strong> can take
                bookings and payments in the app.
              </span>
            </li>
          </ul>
        </div>
      </section>

      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl font-bold mb-4 text-center">
            How it works for professionals
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-6">
            Graduates and independent pros join as{" "}
            <strong className="text-gray-300">professionals</strong> and pick a
            membership that suits their stage of business.
          </p>
          <p className="text-center text-gray-300 text-sm md:text-base max-w-2xl mx-auto mb-10 bg-gray-900/80 rounded-lg py-4 px-4 border border-gray-800">
            <strong className="text-green-400">
              Graduates who sign up through your training provider
            </strong>{" "}
            receive{" "}
            <strong className="text-white">
              6 months of Full Membership at no cost
            </strong>{" "}
            — the complete package below (map, bookings, Stripe, payouts,
            analytics) — so they can{" "}
            <strong className="text-white">kick-start their careers</strong> with
            the same tools as established pros. That is twice the standard
            introductory period offered elsewhere on the platform.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <h3 className="text-xl font-semibold text-green-400 mb-3">
                Map Only
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                Strong option for visibility and conversations without taking
                payments in-app.
              </p>
              <ul className="text-gray-300 text-sm space-y-2 list-disc list-inside">
                <li>Profile on the map, messaging, reviews</li>
                <li>Rich profile (photos, videos), services</li>
                <li>View client profiles and connect</li>
              </ul>
            </div>
            <div className="bg-gray-900 rounded-xl p-6 border border-green-900/40">
              <h3 className="text-xl font-semibold text-green-400 mb-3">
                Full Membership
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                For pros who want bookings, calendar, and payouts in one flow.
              </p>
              <ul className="text-gray-300 text-sm space-y-2 list-disc list-inside">
                <li>Everything in Map Only</li>
                <li>Unlimited bookings, availability, calendar</li>
                <li>Stripe Connect, payouts, booking notifications</li>
                <li>Booking history and analytics</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Professionals can also use{" "}
            <strong className="text-gray-400">Fit-Match ID</strong> (shareable
            profile link), <strong className="text-gray-400">referrals</strong>{" "}
            to invite other pros, and in-app analytics.
          </p>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-6 max-w-3xl">
          <h2 className="text-3xl font-bold mb-6 text-center">
            How communities fit in
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed mb-4">
            <strong className="text-white">Communities</strong> on Fit-Match are
            organisations that run group fitness — clubs, run groups, boot camps,
            and similar. They get a presence on the map, can share events and
            schedules, and interact with members through the same ecosystem as
            clients and pros.
          </p>
          <p className="text-gray-300 text-lg leading-relaxed">
            Your graduates might work inside facilities, as solo pros, or
            alongside community organisers — Fit-Match is built so all those
            roles can coexist and discover each other.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 className="text-3xl font-bold mb-4 text-center">
            Training providers on Fit-Match
          </h2>
          <p className="text-gray-400 text-center max-w-2xl mx-auto mb-6">
            Academies and course providers are first-class on the platform — not
            just a logo on a partner page.
          </p>
          <div className="max-w-3xl mx-auto mb-12 bg-black/50 border border-green-700/40 rounded-2xl p-6 md:p-8">
            <h3 className="text-xl font-bold text-green-400 mb-3 text-center">
              Free for you — a standout benefit for your graduates
            </h3>
            <p className="text-gray-300 text-center leading-relaxed mb-4">
              There is <strong className="text-white">no fee</strong> for your
              organisation to partner with Fit-Match. You do not pay for
              listings, links, or the graduate offer. Instead, you get something
              concrete to put in your prospectus and graduation packs:{" "}
              <strong className="text-white">
                six months of Full professional membership, completely free
              </strong>{" "}
              for each graduate who joins via your provider programme.
            </p>
            <p className="text-gray-400 text-sm text-center leading-relaxed">
              That means your students can appear on the map, take enquiries, run
              availability, accept paid bookings, and receive payouts — all
              included — while they build their client base and confidence. It is
              one of the strongest ways you can say you support them after they
              leave the classroom.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                Your own profile
              </h3>
              <p className="text-gray-400 text-sm">
                A full provider profile — imagery, bio, services, reviews,
                messaging — discoverable on the map like professionals and
                facilities.
              </p>
            </div>
            <div className="bg-gray-900 rounded-xl p-6 border border-green-900/30">
              <h3 className="text-lg font-semibold text-white mb-2">
                6 months free — kick-start their careers
              </h3>
              <p className="text-gray-400 text-sm">
                Graduates who register through your partnership link get{" "}
                <strong className="text-gray-300">
                  6 months of Full Membership
                </strong>{" "}
                at no charge: full visibility, messaging, bookings, Stripe
                Connect, and payouts. They hit the ground running instead of
                paying subscriptions while they are still building income.
              </p>
            </div>
            <div className="bg-gray-900 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                Showcase your alumni
              </h3>
              <p className="text-gray-400 text-sm">
                Graduates who register through your link are listed on{" "}
                <strong className="text-gray-300">your organisation&apos;s</strong>{" "}
                Fit-Match profile in the graduate showcase — a living alumni wall
                that grows with each cohort and shows where your training leads.
              </p>
            </div>
            <div className="bg-gray-900 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                Inbound interest
              </h3>
              <p className="text-gray-400 text-sm">
                Prospective students, partners, and professionals can message you
                through the app — one channel for enquiries tied to your visible
                presence.
              </p>
            </div>
          </div>
          <p className="text-center mt-10">
            <Link
              to="/training-providers"
              className="text-blue-fit-match hover:underline font-medium"
            >
              Read the full training provider partnership page →
            </Link>
          </p>
        </div>
      </section>

      <section className="py-16 bg-black">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <h2 className="text-3xl font-bold mb-6">
            Why this helps your students
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            The <strong className="text-gray-300">six free months</strong> are
            there so new professionals can{" "}
            <strong className="text-white">kick-start their careers</strong>{" "}
            without choosing between rent and a subscription — while still
            looking like the real deal to clients from day one.
          </p>
          <ul className="text-left text-gray-300 space-y-4 max-w-xl mx-auto">
            <li className="flex gap-3">
              <span className="text-green-500">✓</span>
              <span>
                <strong className="text-white">Six months on the full plan</strong>{" "}
                — bookings, calendar, and payouts included, not a limited trial.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-500">✓</span>
              <span>
                <strong className="text-white">Credibility</strong> — a real
                profile, reviews, and map presence instead of starting from zero
                online.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-500">✓</span>
              <span>
                <strong className="text-white">Discovery</strong> — clients
                already use the map to find help; graduates appear where people
                search.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-500">✓</span>
              <span>
                <strong className="text-white">Operations</strong> — with Full
                Membership, they can run availability, bookings, and payouts
                without stitching together separate tools.
              </span>
            </li>
            <li className="flex gap-3">
              <span className="text-green-500">✓</span>
              <span>
                <strong className="text-white">Alignment with you</strong> —
                your brand stays visible through your provider profile and
                graduate journey.
              </span>
            </li>
          </ul>
        </div>
      </section>

      <section className="py-16 bg-gray-950 border-t border-gray-800">
        <div className="container mx-auto px-6 max-w-2xl text-center">
          <h2 className="text-2xl font-bold mb-4">Next steps</h2>
          <p className="text-gray-400 mb-8">
            Questions about partnership, your graduates&apos; six-month access,
            or how your cohort should sign up? We are happy to walk through it —
            whether you are ready to go live or just exploring while we grow.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={mailto}
              className="inline-block bg-green-600 text-white font-bold py-3 px-8 rounded-full hover:bg-green-700 transition duration-300"
            >
              Email the team
            </a>
            <a
              href={WEB_APP_URL}
              className="inline-block bg-gray-800 text-white font-bold py-3 px-8 rounded-full border border-gray-600 hover:bg-gray-700 transition duration-300"
            >
              Open the Fit-Match app (web)
            </a>
          </div>
          <p className="text-gray-600 text-sm mt-8">
            Share this page:{" "}
            <span className="text-gray-400 font-mono text-xs break-all">
              {shareUrl}
            </span>
          </p>
        </div>
      </section>
    </>
  );
}
